import { Space } from '@sravni/react-design-system';
import type { ReactNode } from 'react';
import React, { useState, useCallback } from 'react';

import { FormStepId } from 'shared/config/formStepId';
import { useAppDispatch, useAppSelector } from 'shared/lib';
import { logFormData } from 'shared/lib/logFormData';
import type { IInitialEvent } from 'shared/lib/sendGAEvents/types';

import type { TCreditFormCommonFields } from 'entities/creditForm';
import {
  isAnyStepOpenedInPopup,
  setFormIsOpenedInPopupByStepId,
  formReadyForSendingSelector,
  selectIsStepsOpenedInPopup,
} from 'entities/creditForm';
import { isLoggedSelector } from 'entities/user';
import { FormIcon, useFormStepDescriptionComponent, useFormStepTitleComponent } from 'entities/whiteLabels';

import { AnketaTitle } from 'features/AnketaTitle';
import { AuthenticationEsia, useIsShowAuthenticationEsia } from 'features/AuthenticationEsia';
import { useSendLoansContactData } from 'features/SendLoansContactData';
import { useSendFirstTouch } from 'features/SendLoansFirstTouch';
import { useSendPreApprovalsSearchApplication } from 'features/SendPreApprovalsSearchApplication';
import { useUpdateScoreValue } from 'features/UpdateScoreValue';
import { useFormStepHeadersConfig } from 'features/useFormStepHeadersConfig';

import { submitAnketaThunk } from 'widgets/AnketaWidget/lib/submitAnketaThunk';
import { updateUserDataThunk } from 'widgets/AnketaWidget/lib/updateUserDataThunk';
import { useValidateData } from 'widgets/AnketaWidget/lib/useValidateData';

import { useStepper, getValidationSteps } from '../../lib';
import type { IOrderedStep } from '../../types';
import { WIDGET_BLOCKS } from '../AnketaWidget.config';

export type TAnketaProps = {
  isLoading?: boolean;
  onFormComplete?: (fields: TCreditFormCommonFields) => void;
  isHideRestoredData?: boolean;
  isHideStepDivider?: boolean;
  accordionIconSize?: 36 | 48;
  initialOpenStepId?: ValuesOf<typeof FormStepId>; // stepId, который нужно сразу открыть, если шаги до этого валидны
  analyticsOverrides?: Partial<Pick<IInitialEvent, 'event' | 'eventCategory'>>;
  isEnabledValidationOnMount?: boolean;
};

// eslint-disable-next-line max-statements
export const Anketa: FC<TAnketaProps> = (props) => {
  const {
    isLoading,
    onFormComplete,
    isHideRestoredData,
    isHideStepDivider,
    accordionIconSize,
    initialOpenStepId,
    analyticsOverrides,
    isEnabledValidationOnMount,
  } = props;
  const { activeStep, lastStep, stepsWithMultiStepsOrder } = useStepper();
  const dispatch = useAppDispatch();
  const isFormReady = useAppSelector(formReadyForSendingSelector);
  const isAnyFormStepOpenedInPopup = useAppSelector(isAnyStepOpenedInPopup);
  const stepsInPopup = useAppSelector(selectIsStepsOpenedInPopup);
  const sendContact = useSendLoansContactData();
  const isAuth = useAppSelector(isLoggedSelector);
  const TitleComponent = useFormStepTitleComponent();
  const DescriptionComponent = useFormStepDescriptionComponent();
  const { headersConfig: stepDefaultHeaders, isAlwaysShowDescription } = useFormStepHeadersConfig();
  const checkIsShowEsiaLoginBlock = useIsShowAuthenticationEsia();
  const sendPreApprovalsApplication = useSendPreApprovalsSearchApplication();
  const sendFirstTouch = useSendFirstTouch();
  const [isFirstValidation, setIsFirstValidation] = useState(true);
  const validationSteps = getValidationSteps(stepsWithMultiStepsOrder, {
    isEnabled: isEnabledValidationOnMount,
    isAuth,
    initialOpenStepId,
    isFirstValidation,
  });

  const onStepValidationSuccess = useCallback(
    async (stepId: ValuesOf<typeof FormStepId>) => {
      if (stepId === 'passportInfo' && isAuth) {
        await sendContact();
        await sendPreApprovalsApplication();
      }
    },
    [sendPreApprovalsApplication, sendContact, isAuth],
  );

  const onFullFormValidationSuccess = useCallback(
    async (data: TCreditFormCommonFields, lastValidatedStepId: ValuesOf<typeof FormStepId>) => {
      const isLastValidatedStep = lastValidatedStepId === lastStep.formId;
      if (!isLastValidatedStep) {
        return;
      }

      logFormData('Result Form SubmitData', data);

      if (onFormComplete) {
        onFormComplete(data);
      }
    },
    [onFormComplete, lastStep.formId],
  );

  const { isValidating, validate } = useValidateData(
    {
      analyticsOverrides,
      onComplete: () => {
        setIsFirstValidation(false);
      },
      onSuccess: onFullFormValidationSuccess,
      onStepSuccess: onStepValidationSuccess,
    },
    validationSteps.length > 0,
    validationSteps,
  );

  useUpdateScoreValue();

  const [shouldScrollIntoAccordion, setShouldScrollIntoAccordion] = useState(false);

  const onFullySubmitCallback = useCallback(
    (data: TCreditFormCommonFields) => {
      logFormData('Result Form SubmitData', data);
      if (onFormComplete) {
        onFormComplete(data);
      }
    },
    [onFormComplete],
  );

  const onFormSubmit = useCallback(
    async (_: unknown, isDialog?: boolean | undefined) => {
      await dispatch(updateUserDataThunk());

      if (activeStep?.formId === 'passportInfo' && isAuth) {
        await sendContact();
      }

      if (
        (activeStep?.formId && FormStepId.PassportInfo === activeStep?.formId) ||
        [FormStepId.CreditPurposeInfo, FormStepId.ContactsInfo, FormStepId.PassportInfo].some(
          (step) => stepsInPopup[step],
        )
      ) {
        await sendPreApprovalsApplication();
      }

      dispatch(submitAnketaThunk(isDialog, onFullySubmitCallback));
      setShouldScrollIntoAccordion(true);
    },
    [activeStep, isAuth, sendPreApprovalsApplication, sendContact, onFullySubmitCallback, dispatch, stepsInPopup],
  );

  const onPopupClose = useCallback(
    (stepId: FormStepId) => () => {
      dispatch(setFormIsOpenedInPopupByStepId({ stepId, isOpened: false }));
    },
    [dispatch],
  );

  const setIsStepOpenInPopupById = useCallback(
    (formId: FormStepId) => () => {
      dispatch(setFormIsOpenedInPopupByStepId({ stepId: formId, isOpened: true }));
    },
    [dispatch],
  );

  const onAuthorizationSuccess = useCallback(() => {
    sendFirstTouch();
    validate();
  }, [sendFirstTouch, validate]);

  const getStep = useCallback(
    (step: IOrderedStep, index: number): ReactNode => {
      const { formStepComponent: Component, submitButtonText } = WIDGET_BLOCKS[step.formId as FormStepId];
      const isLastStep = lastStep.formId === step.formId;
      const isOpen = activeStep?.formId === step.formId || (isFormReady && isLastStep);
      const isDisableEdit = typeof activeStep?.stepIndex !== 'undefined' && step.stepIndex > activeStep?.stepIndex;
      const { title, description } = stepDefaultHeaders[step.formId as FormStepId];
      const onCloseStepPopup = onPopupClose(step.formId);
      const onOpenStepPopup = setIsStepOpenInPopupById(step.formId);
      const isShowEsiaBlock = checkIsShowEsiaLoginBlock(step.formId);

      return (
        <Component
          esiaLoginBlock={isShowEsiaBlock ? <AuthenticationEsia step={step.formId} /> : undefined}
          multipartFormId=""
          key={step.formId}
          isOpen={isOpen}
          isLoading={isLoading || isValidating}
          isDisableEdit={isDisableEdit}
          onFormSubmit={onFormSubmit}
          shouldHideDividers={isLastStep}
          shouldRestoreAdditionalData
          shouldScrollIntoAccordion={shouldScrollIntoAccordion}
          stepIndex={index}
          isHideAccordionData={isHideRestoredData}
          formId={step.formId}
          icon={<FormIcon stepName={step.formId} />}
          accordionIconSize={accordionIconSize}
          submitButtonText={submitButtonText}
          isHideDivider={isHideStepDivider}
          TitleComponent={TitleComponent}
          DescriptionComponent={DescriptionComponent}
          defaultTitle={title}
          defaultDescription={description}
          isAlwaysShowDescription={isAlwaysShowDescription}
          onPopupClose={onCloseStepPopup}
          onPopupOpen={onOpenStepPopup}
          isAnyFormStepOpenedInPopup={isAnyFormStepOpenedInPopup}
          onAuthorizationSuccess={onAuthorizationSuccess}
          analyticsOverrides={analyticsOverrides}
          isPopupClosable
        />
      );
    },
    [
      activeStep,
      lastStep.formId,
      isLoading,
      onFormSubmit,
      shouldScrollIntoAccordion,
      isFormReady,
      isHideRestoredData,
      isHideStepDivider,
      accordionIconSize,
      TitleComponent,
      DescriptionComponent,
      stepDefaultHeaders,
      isAlwaysShowDescription,
      onPopupClose,
      isAnyFormStepOpenedInPopup,
      setIsStepOpenInPopupById,
      checkIsShowEsiaLoginBlock,
      isValidating,
      onAuthorizationSuccess,
      analyticsOverrides,
    ],
  );

  return (
    <Space direction="vertical">
      <AnketaTitle />
      {stepsWithMultiStepsOrder.map(getStep)}
    </Space>
  );
};
